import { BEIGE_600, GRAY_400, GRAY_900, WHITE } from 'constants/styling/theme'
import { Box, ClickAwayListener, Paper, Stack, Typography } from '@mui/material'
import { FC, useCallback, useMemo, useState } from 'react'
import i18n from 'translations/i18n'
import { GooglePlaceComponent, getFromGooglePlace } from 'utils/location/googlePlaceUtils'

import { Country, REGISTRATION_COUNTRIES } from 'constants/country'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { getGoogleApiKey } from 'constants/API'
import { GoogleAPIPlace } from 'models/purchaseFlow'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUIInputField } from 'components/common/MUIInputField'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import usePlacesAutocompleteService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'
import { useTranslation } from 'react-i18next'

export interface AddressFields {
  street: string
  zip: string
  city: string
  country: Country
}

export interface AddressAutocompleteProps {
  street: string
  zip: string
  city: string
  country: Country
  onStreetChange: (value: string) => void
  onZipChange: (value: string) => void
  onCityChange: (value: string) => void
  onCountryChange: (value: Country) => void
  onPlaceSelect: (fields: AddressFields) => void
  defaultMode?: 'search' | 'manual'
}

export const AddressAutocomplete: FC<AddressAutocompleteProps> = ({
  street,
  zip,
  city,
  country,
  onStreetChange,
  onZipChange,
  onCityChange,
  onCountryChange,
  onPlaceSelect,
  defaultMode = 'search',
}) => {
  const { t } = useTranslation(['signup', 'country'])
  const [mode, setMode] = useState<'search' | 'manual'>(defaultMode)
  const [searchQuery, setSearchQuery] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const {
    placePredictions,
    getPlacePredictions,
    placesService,
    isPlacePredictionsLoading,
  } = usePlacesAutocompleteService({
    apiKey: getGoogleApiKey(),
    debounce: 300,
    sessionToken: true,
    language: i18n.language?.split('-')[0] || 'en',
    options: {
      input: '',
      types: ['address'],
    },
  })

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchQuery(value)

    if (value.length > 0) {
      getPlacePredictions({ input: value })
      setIsDropdownOpen(true)
    } else {
      setIsDropdownOpen(false)
    }
  }, [getPlacePredictions])

  const handlePlaceSelect = useCallback((placeId: string) => {
    if (!placesService) return

    placesService.getDetails(
      { placeId, fields: ['address_components'] },
      (placeDetails) => {
        if (!placeDetails?.address_components) return

        const googlePlace = { address_components: placeDetails.address_components } as GoogleAPIPlace

        const streetName = getFromGooglePlace(googlePlace, GooglePlaceComponent.street)
        const streetNumber = getFromGooglePlace(googlePlace, GooglePlaceComponent.streetNumber)
        const postalCode = getFromGooglePlace(googlePlace, GooglePlaceComponent.postalCode)
        const cityName = getFromGooglePlace(googlePlace, GooglePlaceComponent.city)
        const countryCode = getFromGooglePlace(googlePlace, GooglePlaceComponent.country)

        const fullStreet = streetNumber ? `${streetName} ${streetNumber}` : streetName
        const resolvedCountry = Country[countryCode as Country] || country

        onPlaceSelect({
          street: fullStreet,
          zip: postalCode,
          city: cityName,
          country: resolvedCountry,
        })

        setSearchQuery('')
        setIsDropdownOpen(false)
        setMode('manual')
      }
    )
  }, [placesService, country, onPlaceSelect])

  const handleManualModeSwitch = useCallback(() => {
    setSearchQuery('')
    setIsDropdownOpen(false)
    setMode('manual')
  }, [])

  const handleSearchModeSwitch = useCallback(() => {
    setSearchQuery('')
    setMode('search')
  }, [])

  const showDropdown = useMemo(() =>
    isDropdownOpen && (placePredictions.length > 0 || isPlacePredictionsLoading || searchQuery.length > 0),
  [isDropdownOpen, placePredictions.length, isPlacePredictionsLoading, searchQuery.length])

  return (
    <>
      {mode === 'search' &&
        <ClickAwayListener onClickAway={() => setIsDropdownOpen(false)}>
          <Box position="relative">
            <MUIInputField
              required
              label={t('signup:address')}
              value={searchQuery}
              placeholder={t('signup:address_search_placeholder')}
              startIcon={<SearchOutlinedIcon sx={{ fontSize: '2.4rem', marginRight: '-.4rem' }} />}
              onChange={handleSearchChange}
            />

            {showDropdown &&
              <Paper
                elevation={3}
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  marginTop: '.4rem',
                  borderRadius: '.8rem',
                  overflow: 'hidden',
                  maxHeight: '24rem',
                  overflowY: 'auto',
                }}
              >
                {placePredictions.map((prediction) => (
                  <Box
                    key={prediction.place_id}
                    onClick={() => handlePlaceSelect(prediction.place_id)}
                    sx={{
                      padding: '.8rem 1.2rem',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: GRAY_400,
                      },
                    }}
                  >
                    <Typography variant="text-sm" color={GRAY_900}>
                      {prediction.description}
                    </Typography>
                  </Box>
                ))}

                <Box
                  onClick={handleManualModeSwitch}
                  sx={{
                    padding: '.8rem 1.2rem',
                    cursor: 'pointer',
                    borderTop: `1px solid ${GRAY_400}`,
                    '&:hover': {
                      backgroundColor: GRAY_400,
                    },
                  }}
                >
                  <Typography variant="text-md" color={GRAY_900} fontWeight={550}>
                    {t('signup:address_add_manually')}
                  </Typography>
                </Box>
              </Paper>
            }
          </Box>
        </ClickAwayListener>
      }

      {mode === 'manual' &&
        <Stack gap="1.6rem">
          <MUIInputField
            required
            label={t('signup:address_street')}
            value={street}
            onChange={e => onStreetChange(e.target.value)}
          />

          <Stack gap=".8rem" flexDirection="row" justifyContent="space-between">
            <MUIInputField
              required
              label={t('signup:address_zip')}
              value={zip}
              onChange={e => onZipChange(e.target.value)}
            />

            <MUIInputField
              required
              label={t('signup:address_city')}
              value={city}
              onChange={e => onCityChange(e.target.value)}
            />
          </Stack>

          <MUIDropdown
            sx={{ zIndex: 2 }}
            button={(isOpen, action, onClose) => (
              <MUIDropdownInput
                readOnly
                required
                label={t('signup:country')}
                type={DropdownInputTypes.SELECTOR}
                value={country ? t(`country:${country}`) : country}
                onClick={(e) => {
                  if (isOpen) return onClose()
                  action(e)
                }}
              />
            )}
          >
            <Box height="30rem">
              {REGISTRATION_COUNTRIES.map(countryKey => (
                <MUIDropdownItem key={countryKey} onClick={() => onCountryChange(countryKey)}>
                  <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                    {t(`country:${countryKey}`, countryKey)}
                  </Typography>
                </MUIDropdownItem>
              ))}
            </Box>
          </MUIDropdown>

          <Box
            onClick={handleSearchModeSwitch}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: 'flex-start',
              padding: '.2rem 1rem',
              borderRadius: '4rem',
              border: `1px solid ${BEIGE_600}`,
              backgroundColor: WHITE,
              cursor: 'pointer',
              '&:hover': { backgroundColor: BEIGE_600 },
            }}
          >
            <Typography variant="text-sm" color={GRAY_900} fontWeight={550}>
              {t('signup:address_go_back_to_search')}
            </Typography>
          </Box>
        </Stack>
      }
    </>
  )
}
